import type { ConnectorDTO } from "../../shared/types";
import { toolName } from "../lib/scopes";
import { connectorBrand } from "./AgentEditor";
import { BrandIcon } from "./BrandIcon";
import { pluralWord } from "../../shared/scope-words";
import { Badge, Button, Field, inputCls } from "./ui";

export type ToolField = { system: string; idField: string };
export type TypeDraft = { key: string; name: string; pluralName: string; description: string; fields: ToolField[] };

/** A type's key and its tools are what the server accepts: lowercase letters, digits and underscores. */
const KEY = /^[a-z][a-z0-9_]{0,63}$/;
const ID_FIELD = /^[A-Za-z0-9_.-]{1,64}$/;
/** A tool is a connector's slug, as the server takes it. */
const TOOL = /^[a-z0-9][a-z0-9_-]{0,63}$/;

/** From a name: a key that the type part of every scope key can be. */
const keyFrom = (name: string) => name.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^[^a-z]+|_+$/g, "").slice(0, 64);

export const typeReady = (d: TypeDraft) =>
  KEY.test(d.key) && d.name.trim().length > 0 && d.pluralName.trim().length > 0 && d.fields.every((f) => TOOL.test(f.system) && ID_FIELD.test(f.idField.trim()));

/** A tool's mark: its connector's brand, or a plain plug for a tool no connector has. */
export function ToolMark({ system, connectors, className = "size-4" }: { system: string; connectors: ConnectorDTO[]; className?: string }) {
  const connector = connectors.find((c) => c.slug === system);
  return <BrandIcon brand={connector ? connectorBrand(connector) : system} className={className} />;
}

/** "SalesOS · account_id": one tool of a type and what its id is called there. */
export function ToolChip({ field, connectors, main = false }: { field: ToolField; connectors: ConnectorDTO[]; main?: boolean }) {
  return (
    <span title={main ? "Main tool" : undefined} className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-fill py-1 pr-2.5 pl-1 text-xs text-muted">
      <span className="grid size-5 shrink-0 place-items-center rounded-full bg-raised">
        <ToolMark system={field.system} connectors={connectors} className="size-3.5" />
      </span>
      <span className="truncate">
        {toolName(connectors, field.system)} · <span className="font-mono">{field.idField}</span>
      </span>
    </span>
  );
}

/** The tools a type's scopes have an id in, in order: the first is the main tool. */
function ToolsEditor({ fields, connectors, typeKey, onChange }: { fields: ToolField[]; connectors: ConnectorDTO[]; typeKey: string; onChange: (fields: ToolField[]) => void }) {
  // Every connector can be a type's tool; a tool no connector has keeps its own row.
  const usable = connectors;
  const unused = usable.filter((c) => !fields.some((f) => f.system === c.slug));
  const set = (i: number, patch: Partial<ToolField>) => onChange(fields.map((f, j) => (j === i ? { ...f, ...patch } : f)));
  const move = (i: number, by: number) => {
    const next = [...fields];
    const [field] = next.splice(i, 1);
    next.splice(i + by, 0, field!);
    onChange(next);
  };
  const small = "px-2 py-1 text-xs";
  return (
    <div className="space-y-2">
      {fields.map((f, i) => (
        <div key={i} className="flex flex-wrap items-center gap-2 rounded-xl bg-fill-subtle p-2.5">
          <span className="grid size-8 shrink-0 place-items-center rounded-full bg-raised shadow-sm ring-1 ring-line">
            <ToolMark system={f.system} connectors={connectors} />
          </span>
          <select className={`${inputCls} w-auto min-w-0 flex-1 sm:w-44 sm:flex-none`} value={f.system} onChange={(e) => set(i, { system: e.target.value })} aria-label={`Tool ${i + 1}`}>
            {!usable.some((c) => c.slug === f.system) && <option value={f.system}>{f.system || "Choose a tool"}</option>}
            {usable.map((c) => (
              <option key={c.id} value={c.slug} disabled={c.slug !== f.system && fields.some((g) => g.system === c.slug)}>
                {c.name}
              </option>
            ))}
          </select>
          <input
            className={`${inputCls} order-last basis-full font-mono sm:order-none sm:basis-0 sm:flex-1`}
            value={f.idField}
            placeholder={`${typeKey || "account"}_id`}
            onChange={(e) => set(i, { idField: e.target.value })}
            aria-label={`What the id is called in ${toolName(connectors, f.system)}`}
          />
          <div className="ml-auto flex shrink-0 items-center justify-end gap-0.5 sm:w-36">
            {i === 0 ? (
              <span className="shrink-0 whitespace-nowrap">
                <Badge tone="blue">Main tool</Badge>
              </span>
            ) : (
              <Button type="button" variant="ghost" className={small} onClick={() => move(i, -1)} aria-label="Move up">↑</Button>
            )}
            {i < fields.length - 1 && <Button type="button" variant="ghost" className={small} onClick={() => move(i, 1)} aria-label="Move down">↓</Button>}
            <Button type="button" variant="ghost" className={`${small} text-muted`} onClick={() => onChange(fields.filter((_, j) => j !== i))} aria-label="Remove tool">✕</Button>
          </div>
        </div>
      ))}
      <Button type="button" disabled={unused.length === 0} onClick={() => onChange([...fields, { system: unused[0]!.slug, idField: "" }])}>
        Add a tool
      </Button>
      {usable.length === 0 && <p className="text-xs text-muted">No connectors yet: add the tools on the Connectors page first.</p>}
    </div>
  );
}

/**
 * A type's name, plural, key, description and tools. The key is set once, when the type is made. The name, plural and
 * description are read by every agent working per the type, in its system prompt (AIT-27).
 */
export function TypeForm({ draft, connectors, isNew, onChange }: { draft: TypeDraft; connectors: ConnectorDTO[]; isNew: boolean; onChange: (draft: TypeDraft) => void }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Name" hint="What people call it, e.g. Account.">
          <input
            className={inputCls}
            value={draft.name}
            placeholder="Account"
            // Until the key and the plural are typed by hand, they follow the name.
            onChange={(e) =>
              onChange({
                ...draft,
                name: e.target.value,
                ...(isNew && draft.key === keyFrom(draft.name) ? { key: keyFrom(e.target.value) } : {}),
                ...(!draft.pluralName || draft.pluralName === pluralWord(draft.name.trim()) ? { pluralName: e.target.value.trim() ? pluralWord(e.target.value.trim()) : "" } : {}),
              })
            }
          />
        </Field>
        <Field label="Plural" hint="How its agents read several of them, e.g. Accounts.">
          <input className={inputCls} value={draft.pluralName} placeholder="Accounts" onChange={(e) => onChange({ ...draft, pluralName: e.target.value })} />
        </Field>
        {isNew && (
          <Field label="Key" hint="The first part of every scope key, as in account:…; it never changes.">
            <input className={`${inputCls} font-mono`} value={draft.key} placeholder="account" onChange={(e) => onChange({ ...draft, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") })} />
          </Field>
        )}
      </div>
      <Field label="Description" hint={`What one ${draft.name.trim().toLowerCase() || "scope of this type"} is. Every agent working per ${draft.name.trim().toLowerCase() || "this type"} reads this.`}>
        <textarea className={inputCls} rows={2} value={draft.description} onChange={(e) => onChange({ ...draft, description: e.target.value })} />
      </Field>
      <div>
        <div className="mb-1.5 text-xs font-medium text-muted">Tools</div>
        <ToolsEditor fields={draft.fields} connectors={connectors} typeKey={draft.key} onChange={(fields) => onChange({ ...draft, fields })} />
        <p className="mt-1.5 text-xs text-muted">
          Each tool its scopes have an id in, and what that id is called there (SalesOS → account_id). The first is the main tool: an id given without a scope is looked up there.
        </p>
      </div>
    </div>
  );
}
